// In-meeting panel preview so the FR-X4 / FR-M5 path can be exercised without
// sideloading the Chrome extension (PR #36). Same panel component the
// extension will mount. Meet-shaped chrome from design/ExtensionMeeting.dc.html.
// A test harness, not a feature: it records nothing, so it is Grackle staff only (FR-AD7's
// platform-admin check) and members get the Live notes how-to page instead.

import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { liveNotesPath } from "../library/routes";
import { fetchIsPlatformAdmin } from "../ops/platformAdmin";
import { AppShell } from "../components/AppShell";
import MeetingsTabs from "../library/MeetingsTabs";
import { PageLoader } from "../components/ui";
import { useSession } from "../session";
import MeetingCapturePanel from "../live/MeetingCapturePanel";
import ConsentAnnouncement from "../live/ConsentAnnouncement";
import { api } from "../api/client";
import type { OrgPolicyOut } from "../api/types";
import {
  announceConsent,
  createLiveSession,
  degradeLive,
  finalizeLive,
  markMoment,
  postAudioChunk,
  resumeLive,
  subscribeLiveTranscript,
} from "../live/client";
import { mergeLiveLine } from "../live/mergeLines";
import { encodeS16leBase64 } from "../live/audioTap";
import type { LiveBookmark, LiveSession, TranscriptLine } from "../live/types";
import "../live/live.css";
import { Button, Callout, Input } from "@grackle/ui";

export default function LiveMeetingPreview() {
  const { slug } = useParams();
  const { orgs, user, loading } = useSession();
  const org = orgs.find((o) => o.slug === slug) ?? orgs[0];

  const [session, setSession] = useState<LiveSession | null>(null);
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  const [bookmarks, setBookmarks] = useState<LiveBookmark[]>([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [grackleId, setGrackleId] = useState("");
  const [watchPath, setWatchPath] = useState<string | null>(null);
  const [policy, setPolicy] = useState<OrgPolicyOut | null>(null);
  const [needsConsent, setNeedsConsent] = useState(false);
  const startedAt = useRef<number>(0);
  const seqRef = useRef(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isStaff, setIsStaff] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) return;
    let live = true;
    void fetchIsPlatformAdmin(user.id).then((v) => live && setIsStaff(v));
    return () => {
      live = false;
    };
  }, [user?.id]);

  useEffect(() => {
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  useEffect(() => {
    if (!org) return;
    api<OrgPolicyOut>("GET", `/orgs/${org.id}/policy`)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [org?.id]);

  async function start(fromConsent = false, announcedAt: string | null = null) {
    if (!org) return;
    if (policy?.meeting_require_consent_prompt && !fromConsent) {
      setNeedsConsent(true);
      return;
    }
    setError(null);
    setWatchPath(null);
    try {
      if (policy?.meeting_require_consent_prompt) {
        await api("POST", `/orgs/${org.id}/meeting-consent/ack`, {
          meet_url: "https://meet.google.com/preview",
        });
      }
      const created = await createLiveSession(org.id, {
        title: "Meet live transcript preview",
        meet_url: "https://meet.google.com/preview",
        consent_acknowledged: Boolean(policy?.meeting_require_consent_prompt),
      });
      // F17 §4: the session exists now, so log the "I've announced it" confirmation on it.
      if (announcedAt) await announceConsent(created.id).catch(() => {});
      setSession(created);
      setLines(created.lines);
      setBookmarks(created.bookmarks);
      setNeedsConsent(false);
      startedAt.current = Date.now();
      if (timer.current) clearInterval(timer.current);
      timer.current = setInterval(() => setElapsedMs(Date.now() - startedAt.current), 250);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start a live session.");
    }
  }

  useEffect(() => {
    if (!session) return;
    return subscribeLiveTranscript(session.id, (msg) => {
      if (msg.type === "transcript") {
        setLines((prev) => mergeLiveLine(prev, msg.line));
      }
      if (msg.type === "transcript.final") {
        // F17 §3: the post-call transcript (diarized) replaces the provisional live lines.
        setLines(
          msg.lines.map((l, i) => ({
            seq: i + 1,
            t_start_ms: l.t_start_ms,
            t_end_ms: l.t_end_ms,
            speaker_label: l.speaker_label ?? "Speaker ?",
            speaker_id: null,
            text: l.text,
            is_final: true,
          })),
        );
      }
      if (msg.type === "live_status") {
        setSession((prev) => (prev ? { ...prev, status: msg.status as LiveSession["status"], message: msg.message ?? prev.message } : prev));
      }
    });
  }, [session?.id]);

  function previewPcm(): { b64: string; peak: number } {
    const samples = new Float32Array(16_000);
    for (let i = 0; i < samples.length; i++) {
      samples[i] = Math.sin((2 * Math.PI * 440 * i) / 16_000) * 0.35;
    }
    return encodeS16leBase64(samples);
  }

  async function emitStubChunk() {
    if (!session) return;
    seqRef.current += 1;
    const tMs = Date.now() - startedAt.current;
    const { b64, peak } = previewPcm();
    try {
      const res = await postAudioChunk(session.id, {
        seq: seqRef.current,
        t_ms: tMs,
        peak,
        sample_rate: 16_000,
        channels: 1,
        pcm_s16le_b64: b64,
      });
      if (res.line) setLines((prev) => mergeLiveLine(prev, res.line!));
      if (res.status) {
        setSession((prev) => (prev ? { ...prev, status: res.status as LiveSession["status"] } : prev));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Audio chunk failed.");
    }
  }

  async function onMark() {
    if (!session) return;
    const bookmark = await markMoment(session.id, Date.now() - startedAt.current, "Marked in preview");
    setBookmarks((prev) => [...prev, bookmark]);
  }

  async function onDegrade() {
    if (!session) return;
    const next = await degradeLive(session.id, "cpu");
    setSession(next);
  }

  async function onResume() {
    if (!session) return;
    const next = await resumeLive(session.id);
    setSession(next);
  }

  async function onStop() {
    if (!session) return;
    if (timer.current) clearInterval(timer.current);
    const transcript = await finalizeLive(session.id, grackleId || undefined);
    setSession((prev) => (prev ? { ...prev, status: (transcript.live_status as LiveSession["status"]) ?? "ready" } : prev));
    if (grackleId) setWatchPath(`/g/${grackleId}`);
  }

  if (loading || (user && isStaff === null)) return <PageLoader label="Loading live meeting…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;
  if (!isStaff) return <Navigate to={liveNotesPath(org.slug)} replace />;

  const body = (
    <div className="live-preview-page">
      <MeetingsTabs slug={org.slug} current="live" />
      <header className="page-head">
        <div>
          <h1>Live panel test harness</h1>
          <p>
            {/* FR-X4: the extension's live panel. SD28: the same WebSocket and REST it uses. */}
            Staff only. Drives the extension's live panel against a real live session on this server, over the same
            connection the extension uses, without recording anything. Emit sends 1 s of a 16 kHz test tone to whichever
            speech-to-text slot resolves for you; the dev stub returns placeholder lines, and a real provider usually
            transcribes a tone as nothing. Signed in as {user.name}.
          </p>
        </div>
      </header>

      <div className="live-preview-controls">
        <Button variant="primary" onClick={() => void start()} disabled={!org || Boolean(session) || needsConsent}>
          Start live session
        </Button>
        <Button variant="secondary" onClick={() => void emitStubChunk()} disabled={!session}>
          Emit audio chunk
        </Button>
        <Button variant="secondary" onClick={() => void onDegrade()} disabled={!session}>
          {/* FR-M12: degrade live speech-to-text without ending the session. */}
          Pause transcription
        </Button>
        <Button variant="secondary" onClick={() => void onResume()} disabled={!session}>
          Resume
        </Button>
        <Input
          aria-label="Grackle id"
          placeholder="Optional grackle id to attach on stop"
          value={grackleId}
          onChange={(e) => setGrackleId(e.target.value.trim())}
          wrapperStyle={{ width: 320 }}
        />
      </div>
      {needsConsent && policy ? (
        <ConsentAnnouncement
          consentMode={policy.meeting_consent_mode}
          orgName={org?.name}
          onConfirm={(announcedAt) => void start(true, announcedAt)}
          onCancel={() => setNeedsConsent(false)}
          recordLabel="Start live session"
        />
      ) : null}
      {error ? <Callout tone="danger" icon="warning-circle">{error}</Callout> : null}
      {watchPath ? (
        <div>
          Finalized. Open{" "}
          <Link to={watchPath}>the grackle transcript</Link>.
        </div>
      ) : null}

      <div className="live-preview-meet">
        <Callout tone="record" icon="record">
          Grackle is recording this meeting — everyone here can see this banner
        </Callout>
        <div className="live-preview-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} />
          ))}
        </div>
        <div className="live-preview-overlay">
          <MeetingCapturePanel
            elapsedMs={elapsedMs}
            status={session?.status ?? "unavailable"}
            lines={lines}
            bookmarks={bookmarks}
            message={session?.message}
            onMark={session ? () => void onMark() : undefined}
            onStop={session ? () => void onStop() : undefined}
            stopLabel="Stop"
          />
        </div>
      </div>
    </div>
  );

  return (
    <AppShell org={org} user={user} section="meetings">
      {body}
    </AppShell>
  );
}
